/*
 * A strategy refresh of a campaign whose allocation is APPROVED sends the
 * approved rows the campaign holds — never the saved planner form's copy.
 *
 * Campaign 53 (The Action Bible — Christmas CTV 2026), 2026-09-24: allocation
 * approval writes the approved rows to the campaign
 * (strategy_payload.requestedChannelAllocation) but not to the saved planner
 * form, so a Full Refresh built from the form would have submitted
 * `requestedChannelAllocation: []`. The backend is the authority — it uses the
 * approved allocation and refuses a request that tries to change it — and this
 * keeps the request Quantum sends honest (and its durable-operation
 * fingerprint the same as a canonical request's).
 *
 * The rows sent are the campaign's committed rows only while they still state
 * an APPROVED allocation_authority_v2's money: right after an in-session
 * approval (whose response does not carry the rows it committed) the loaded
 * copy can be the superseded split. Without such an authority to compare
 * against (a legacy approval, which the backend may no longer hold in force)
 * no rows are sent either. Either way the form's copy is dropped and the
 * backend supplies the approved rows itself.
 */

export const ALLOCATION_REQUEST_FIELDS = Object.freeze([
  "requestedChannelAllocation",
  "requested_channel_allocation",
  "channelAllocation",
  "channel_allocation",
  "channelAllocations",
  "channel_allocations",
]);

// Free-text allocation summaries: a stale one could describe an old split.
export const ALLOCATION_SUMMARY_REQUEST_FIELDS = Object.freeze([
  "channelAllocationSummary",
  "channel_allocation_summary",
  "allocationSummary",
  "allocation_summary",
  "budgetAllocationSummary",
  "budget_allocation_summary",
  "platformAllocationSummary",
  "platform_allocation_summary",
]);

const isApproved = (record) =>
  String(record?.allocationApprovalStatus || "").trim().toUpperCase() === "APPROVED";

export const hasApprovedAllocation = ({ allocationAuthority = null, allocationRecommendation = null } = {}) =>
  isApproved(allocationAuthority) || isApproved(allocationRecommendation);

const platformKeyOf = (row) =>
  String(row?.key ?? row?.platformKey ?? row?.platform ?? "").trim().toLowerCase();

// Gross cents per platform of an APPROVED v2 authority's funded rows; null when
// there is no such authority (a legacy approval) or it funds nothing.
const approvedAuthorityGrossCents = (authority) => {
  if (!isApproved(authority) || !Array.isArray(authority?.rows)) return null;
  const byKey = new Map();
  authority.rows.forEach((row) => {
    const cents = Number(row?.grossAmountCents);
    if (!row || row.excluded === true || !Number.isFinite(cents) || cents <= 0) return;
    byKey.set(platformKeyOf(row), Math.round(cents));
  });
  return byKey.size ? byKey : null;
};

const rowsStateApprovedMoney = (rows, approvedGrossCents) => {
  if (!approvedGrossCents) return false;
  if (rows.length !== approvedGrossCents.size) return false;
  return rows.every((row) => {
    const key = platformKeyOf(row);
    const cents = Math.round(Number(row?.grossAmount) * 100);
    return (
      approvedGrossCents.has(key) &&
      Number.isFinite(cents) &&
      Math.abs(approvedGrossCents.get(key) - cents) <= 1
    );
  });
};

/**
 * The refresh request with every allocation spelling (top level and formData)
 * replaced by the campaign's approved rows. Without approved authority, or for
 * a request that targets no existing campaign, the request is returned as-is.
 * When the rows are not loaded — or do not state an approved v2 authority's
 * money — the allocation is omitted, so the backend supplies it itself.
 */
export const applyApprovedAllocationToRefreshRequest = (
  payload,
  {
    campaignId = null,
    allocationAuthority = null,
    allocationRecommendation = null,
    canonicalAllocation = null,
  } = {}
) => {
  if (!payload || typeof payload !== "object" || !campaignId) return payload;
  if (!hasApprovedAllocation({ allocationAuthority, allocationRecommendation })) return payload;
  const rows =
    Array.isArray(canonicalAllocation) &&
    canonicalAllocation.length &&
    rowsStateApprovedMoney(canonicalAllocation, approvedAuthorityGrossCents(allocationAuthority))
      ? canonicalAllocation
      : null;
  const governed = (source) => {
    const next = { ...source };
    [...ALLOCATION_REQUEST_FIELDS, ...ALLOCATION_SUMMARY_REQUEST_FIELDS].forEach((field) => {
      delete next[field];
    });
    if (rows) {
      next.requestedChannelAllocation = rows.map((row) => ({ ...row }));
      next.requested_channel_allocation = rows.map((row) => ({ ...row }));
    }
    return next;
  };
  const next = governed(payload);
  if (payload.formData && typeof payload.formData === "object" && !Array.isArray(payload.formData)) {
    next.formData = governed(payload.formData);
  }
  return next;
};
